import React, { useState } from 'react';
import {
  X,
  CheckCircle,
  XCircle,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Plus,
  Edit,
  Trash2,
  FileText,
  Loader2,
  Merge,
  Split,
  ArrowUpDown
} from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';

interface ChangeReviewModalProps {
  changeRequest: any;
  isOpen: boolean;
  onClose: () => void;
  onApprove?: () => void;
  onReject?: () => void;
}

const ChangeReviewModal: React.FC<ChangeReviewModalProps> = ({
  changeRequest,
  isOpen,
  onClose,
  onApprove,
  onReject
}) => {
  const [approvalNotes, setApprovalNotes] = useState('');
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  if (!isOpen || !changeRequest) return null;

  const isProcessing = isApproving || isRejecting;

  const handleApprove = async () => {
    setIsApproving(true);
    try {
      const response = await api.post(`/api/analysis/change-request/${changeRequest.id}/apply`, {
        selectedChanges: null // Apply all changes
      });

      toast.success(`✅ Applied ${response.data.appliedCount} change(s) successfully!`);
      if (response.data.failedCount > 0) {
        // Show detailed warning for each failed change (orange for partial approval)
        response.data.failedDetails?.forEach((detail: any) => {
          toast(`⚠️ ${detail.changeType.toUpperCase()} failed: ${detail.error}`, {
            duration: 8000,
            icon: '⚠️',
            style: {
              background: '#f59e0b',
              color: '#fff',
              fontWeight: '500'
            }
          });
        });
      }

      if (onApprove) onApprove();
      onClose();
    } catch (error: any) {
      console.error('Apply error:', error);
      toast.error(error.response?.data?.error || 'Failed to apply changes');
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    if (!approvalNotes.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    setIsRejecting(true);
    try {
      await api.put(`/api/analysis/change-request/${changeRequest.id}/reject`, {
        rejectionNotes: approvalNotes.trim()
      });

      toast.success('Change request rejected');
      if (onReject) onReject();
      onClose();
    } catch (error: any) {
      console.error('Reject error:', error);
      toast.error(error.response?.data?.error || 'Failed to reject change request');
    } finally {
      setIsRejecting(false);
    }
  };

  const getChangeTypeIcon = (type: string) => {
    switch (type) {
      case 'add':
        return <Plus className="w-4 h-4" />;
      case 'modify':
        return <Edit className="w-4 h-4" />;
      case 'delete':
        return <Trash2 className="w-4 h-4" />;
      case 'rename':
        return <FileText className="w-4 h-4" />;
      case 'merge':
        return <Merge className="w-4 h-4" />;
      case 'split':
        return <Split className="w-4 h-4" />;
      case 'reorder':
        return <ArrowUpDown className="w-4 h-4" />;
      default:
        return <AlertTriangle className="w-4 h-4" />;
    }
  };

  const getChangeTypeColor = (type: string) => {
    switch (type) {
      case 'add':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'modify':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'delete':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'rename':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'merge':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'split':
        return 'bg-teal-100 text-teal-800 border-teal-200';
      case 'reorder':
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getConfidenceColor = (score: number) => {
    if (score >= 0.8) return 'text-green-600';
    if (score >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const isPendingStatus = changeRequest.status === 'pending';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Review Change Request</h2>
            {changeRequest.source_reference && (
              <p className="text-sm text-gray-600 mt-1">{changeRequest.source_reference}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="text-sm text-blue-700 font-medium mb-1">Total Changes</div>
              <div className="text-2xl font-bold text-blue-900">{changeRequest.parsed_changes.length}</div>
            </div>
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="text-sm text-green-700 font-medium mb-1">Confidence Score</div>
              <div className={`text-2xl font-bold ${getConfidenceColor(changeRequest.confidence_score)}`}>
                {(changeRequest.confidence_score * 100).toFixed(0)}%
              </div>
            </div>
            <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="text-sm text-purple-700 font-medium mb-1">Affected Steps</div>
              <div className="text-2xl font-bold text-purple-900">{changeRequest.affected_steps.length}</div>
            </div>
          </div>

          {/* Source Content */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">Source Content</h3>
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {changeRequest.source_content}
              </p>
            </div>
          </div>

          {/* Parsed Changes */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">Detected Changes</h3>
            <div className="space-y-3">
              {changeRequest.parsed_changes.map((change: any, index: number) => (
                <div key={index} className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-3">
                    {/* Change Type Badge */}
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getChangeTypeColor(change.type)}`}>
                      {getChangeTypeIcon(change.type)}
                      {change.type.toUpperCase()}
                    </span>

                    <div className="flex-1">
                      {/* Step Number/Title */}
                      {change.stepNumber && (
                        <div className="text-sm font-medium text-gray-900 mb-1">
                          Step {change.stepNumber}: {change.proposedTitle || change.newValue}
                        </div>
                      )}

                      {/* Description */}
                      {change.proposedDescription && (
                        <p className="text-sm text-gray-700 mb-2">{change.proposedDescription}</p>
                      )}

                      {/* Old/New Values for Modify */}
                      {change.type === 'modify' && (
                        <div className="grid grid-cols-2 gap-3 mt-2">
                          <div className="p-2 bg-red-50 border border-red-200 rounded">
                            <div className="text-xs font-medium text-red-700 mb-1">Current:</div>
                            <div className="text-sm text-red-900">{change.oldValue || 'N/A'}</div>
                          </div>
                          <div className="p-2 bg-green-50 border border-green-200 rounded">
                            <div className="text-xs font-medium text-green-700 mb-1">Proposed:</div>
                            <div className="text-sm text-green-900">{change.newValue || 'N/A'}</div>
                          </div>
                        </div>
                      )}

                      {/* Impact Analysis (for DELETE/MERGE) */}
                      {change.impact && (
                        <div className={`mt-3 p-3 border rounded-lg ${
                          change.impact.severity === 'high'
                            ? 'bg-red-50 border-red-300'
                            : change.impact.severity === 'medium'
                            ? 'bg-yellow-50 border-yellow-300'
                            : 'bg-blue-50 border-blue-300'
                        }`}>
                          <div className="flex items-start gap-2">
                            <AlertTriangle className={`w-4 h-4 mt-0.5 ${
                              change.impact.severity === 'high'
                                ? 'text-red-600'
                                : change.impact.severity === 'medium'
                                ? 'text-yellow-600'
                                : 'text-blue-600'
                            }`} />
                            <div className="flex-1">
                              <div className="text-xs font-semibold text-gray-900 mb-1">
                                Impact Analysis ({change.impact.severity.toUpperCase()})
                              </div>
                              {change.impact.warnings.map((warning: string, wIdx: number) => (
                                <div key={wIdx} className="text-xs text-gray-700 mb-1">
                                  • {warning}
                                </div>
                              ))}
                              {change.impact.affectedEntities?.mappings?.length > 0 && (
                                <div className="mt-2 text-xs">
                                  <div className="font-medium text-gray-900 mb-1">Affected SOP Sections:</div>
                                  {change.impact.affectedEntities.mappings.map((mapping: any, mIdx: number) => (
                                    <div key={mIdx} className="text-gray-700 ml-2">
                                      • {mapping.sopTitle}: {mapping.section}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Confidence & Rationale */}
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span className={`font-semibold ${getConfidenceColor(change.confidence)}`}>
                          {(change.confidence * 100).toFixed(0)}% confidence
                        </span>
                        {change.rationale && (
                          <>
                            <span>•</span>
                            <span className="italic">{change.rationale}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Approval Notes */}
          {isPendingStatus && (
            <div>
              <label htmlFor="approvalNotes" className="block text-sm font-medium text-gray-700 mb-2">
                Notes {!isPendingStatus && '(Optional)'}
              </label>
              <textarea
                id="approvalNotes"
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                placeholder="Add notes for approval or rejection (required for rejection)..."
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            Status: <span className="font-medium">{changeRequest.status.replace('_', ' ').toUpperCase()}</span>
          </div>

          {isPendingStatus ? (
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                disabled={isProcessing}
                className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={isProcessing}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 disabled:bg-gray-400 transition-colors"
              >
                {isRejecting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <XCircle className="w-4 h-4" />
                )}
                Reject
              </button>
              <button
                onClick={handleApprove}
                disabled={isProcessing}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:bg-gray-400 transition-colors"
              >
                {isApproving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                Approve
              </button>
            </div>
          ) : (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white font-medium rounded-lg hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangeReviewModal;
